import React, { useState } from "react";
import { Link } from "react-router-dom";

import Alert from "veneer/Alert";
import Box from "veneer/Box";
import Button from "veneer/Button";
import VeneerCard from "veneer/Card";
import CardActions from "veneer/CardActions";
import CardHeader from "veneer/CardHeader";
import DestructiveConfirmationDialog from "veneer/DestructiveConfirmationDialog";
import Divider from "veneer/Divider";
import Stack from "veneer/Stack";
import styled from "veneer/styled";
import Typography from "veneer/Typography";

import { Application } from "cortex/ui/src/fleet/view/application/model";
import { useAppDispatch, useAppSelector } from "cortex/ui/src/hooks";
import { createPromiseApiAction } from "cortex/ui/src/redux-store/api-sagas";
import { EDIT_APPLICATION, icon } from "cortex/ui/src/redux-store/applications";
import { getAuthUser } from "cortex/ui/src/redux-store/auth/selectors";
import { isAdmin } from "cortex/ui/src/redux-store/users/selectors";

interface ApplicationCardProps {
  application: Application;
}

export default function ApplicationCard({ application }: ApplicationCardProps) {
  const dispatch = useAppDispatch();
  const user = useAppSelector(getAuthUser);
  const userIsAdmin = isAdmin(user);

  const {
    id,
    display_name: displayName,
    available,
    author,
    stable_version: stableVersion,
    is_installed: isInstalled,
    required,
    is_own: isOwned,
  } = application;

  const [submittedAppId, setSubmittedAppId] = useState<number | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [uninstallAppModal, setUninstallAppModal] = useState<any | null>(null);

  const handleEditApp = async (app: any, install: boolean) => {
    try {
      setSubmittedAppId(app.id);

      await createPromiseApiAction(dispatch, EDIT_APPLICATION, [
        app.id,
        "install",
        install,
      ]);
    } catch (e) {
      setErrorMessage(
        "Failed to edit the application. Please try again later.",
      );
    } finally {
      setSubmittedAppId(null);
    }
  };

  const handleInstall = (app: any) => {
    handleEditApp(app, true);
  };

  const handleUninstall = () => {
    if (uninstallAppModal) {
      handleEditApp(uninstallAppModal, false);
      setUninstallAppModal(null);
    }
  };

  return (
    <>
      <Card elevation={1} fullWidth unavailable={!available}>
        <Stack direction="column" height="100%">
          <Link
            to={
              stableVersion
                ? `/applications/${id}`
                : `/applications/${id}/testing`
            }
          >
            <Box p={1}>
              <Box
                alignItems="center"
                display="flex"
                height={128}
                justifyContent="center"
              >
                <img alt={displayName} height="100%" src={icon(application)} />
              </Box>
            </Box>
          </Link>
          <Divider />
          <Box flexGrow={1} minHeight={100} textAlign="center">
            <CardHeader
              subheader={`by ${isOwned ? "You" : author}`}
              title={displayName}
            />
          </Box>
          {!available && (
            <div className="text-center">
              <Typography>App Unavailable.</Typography>
            </div>
          )}
          {userIsAdmin && !isOwned && !required && (
            <CardActions dataTestId="veneer-card-actions">
              {isInstalled ? (
                <Button
                  loading={submittedAppId === application.id}
                  onClick={() => setUninstallAppModal(application)}
                  variant="outlined"
                >
                  Uninstall
                </Button>
              ) : (
                <Button
                  loading={submittedAppId === application.id}
                  onClick={() => handleInstall(application)}
                >
                  Install
                </Button>
              )}
            </CardActions>
          )}
          {errorMessage && (
            <Alert dense severity="error">
              {errorMessage}
            </Alert>
          )}
        </Stack>
      </Card>
      <DestructiveConfirmationDialog
        commitLabel="Uninstall"
        onCancel={() => setUninstallAppModal(null)}
        onCommit={handleUninstall}
        open={!!uninstallAppModal}
        title="Are you sure you want to uninstall this app?"
        variant="modal"
      >
        This may affect Fleet or device functionality.
      </DestructiveConfirmationDialog>
    </>
  );
}

const Card = styled(VeneerCard, {
  shouldForwardProp: (prop) => prop !== "unavailable",
})(({ unavailable }: { unavailable?: boolean }) => ({
  filter: unavailable ? "grayscale(100%)" : "none",
}));
